import { Stats, LogsResponse, Settings } from './types';

let currentApiBase = '/api';
let authToken: string | null = null;

export function setApiBase(url: string) {
  let normalized = url.trim();
  if (!normalized.startsWith('http')) {
    normalized = `https://${normalized}`;
  }
  normalized = normalized.replace(/\/$/, '');
  currentApiBase = `${normalized}/api`;
}

function getApiBase(): string {
  return currentApiBase;
}

export function setAuthToken(token: string | null) {
  authToken = token;
  if (token) {
    localStorage.setItem('sinker_token', token);
  } else {
    localStorage.removeItem('sinker_token');
  }
}

export function getAuthToken(): string | null {
  if (authToken) return authToken;
  const stored = localStorage.getItem('sinker_token');
  if (stored) authToken = stored;
  return authToken;
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  // Add authorization header if token exists
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Merge with custom headers
  if (options.headers) {
    Object.assign(headers, options.headers);
  }
  
  const response = await fetch(`${getApiBase()}${endpoint}`, {
    ...options,
    credentials: 'omit',
    headers,
  });

  if (response.status === 401) {
    setAuthToken(null);
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// Auth
export async function login(username: string, password: string): Promise<{ success: boolean; token?: string; firstLogin?: boolean }> {
  const response = await fetch(`${getApiBase()}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
    credentials: 'omit',
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Login failed' }));
    throw new Error(error.error || 'Login failed');
  }
  
  const data = await response.json();
  
  if (data.token) {
    setAuthToken(data.token);
  }
  
  return data;
}

export async function logout(): Promise<{ success: boolean }> {
  const result = await request<{ success: boolean }>('/auth/logout', { method: 'POST' });
  setAuthToken(null);
  return result;
}

// Stats
export async function fetchStats(): Promise<Stats> {
  return request<Stats>('/stats');
}

// Logs
export async function fetchLogs(limit = 100, offset = 0): Promise<LogsResponse> {
  return request<LogsResponse>(`/logs?limit=${limit}&offset=${offset}`);
}

export async function clearLogs(): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('/logs/clear', { method: 'POST' });
}

// Settings
export async function fetchSettings(): Promise<Settings> {
  return request<Settings>('/settings');
}

export async function updateSettings(settings: Partial<Settings>): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('/settings', {
    method: 'POST',
    body: JSON.stringify(settings),
  });
}

// Lists
// Split list endpoints
export async function fetchAllowlist(): Promise<Record<string, string[]>> {
  return request<Record<string, string[]>>('/allowlist');
}

export async function fetchDenylist(): Promise<Record<string, string[]>> {
  return request<Record<string, string[]>>('/denylist');
}

export async function addToList(
  listType: 'allowlist' | 'denylist',
  domain: string,
  folder = ''
): Promise<{ success: boolean; domain: string }> {
  const path = folder ? `/lists/${listType}/${encodeURIComponent(folder)}` : `/lists/${listType}`;
  return request(path, {
    method: 'POST',
    body: JSON.stringify({ domain }),
  });
}

export async function removeFromList(
  listType: 'allowlist' | 'denylist',
  folder: string,
  domain: string
): Promise<{ success: boolean }> {
  const folderPath = folder ? `${encodeURIComponent(folder)}/` : '';
  return request(`/lists/${listType}/${folderPath}${encodeURIComponent(domain)}`, {
    method: 'DELETE',
  });
}

export async function createFolderInList(
  listType: 'allowlist' | 'denylist',
  name: string
): Promise<{ success: boolean; name: string }> {
  return request(`/folders/${listType}`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function deleteFolderFromList(
  listType: 'allowlist' | 'denylist',
  folder: string
): Promise<{ success: boolean }> {
  return request(`/folders/${listType}/${encodeURIComponent(folder)}`, {
    method: 'DELETE',
  });
}

// Handshake
export async function handshake(backendUrl: string): Promise<boolean> {
  try {
    const normalizedUrl = backendUrl.endsWith('/api') 
      ? backendUrl.replace(/\/api$/, '') 
      : backendUrl;
    const response = await fetch(`${normalizedUrl}/api/connect`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) return false;
    const data = await response.json();
    return data.type === 'sinker-dns-gateway';
  } catch {
    return false;
  }
}
